/**
 * Does this workspace have an assistant at all?
 *
 * Every Waslo surface in the dashboard hangs off this one answer: the sidebar group, the
 * two Campaigns rows, the conversation panel and the route guard. Before it existed, all
 * of them rendered for every account administrator the moment the account was created —
 * so a workspace that had just finished Chatwoot onboarding was handed fifteen screens
 * that could only answer `not_provisioned`, and the first thing the product did was fail.
 *
 * THE QUESTION IS ASKED OF THE BFF, NOT INFERRED HERE. `GET …/accounts/:id/provisioning`
 * sits behind the same `requireChatwootAdmin` middleware as every other account route, and
 * that middleware already answers `404 not_provisioned` when the sidecar cannot mint a key
 * for the account (`services/authoring-web/src/routes/api/provisioning.ts`). So the gate
 * and the fifteen screens it gates are the SAME check, and cannot disagree — a gate with
 * its own definition of "provisioned" would eventually hide a section that works.
 *
 * FAIL CLOSED, DELIBERATELY. Unknown, in flight, offline, 403, malformed — every one of
 * them is `false`. Fail-open costs an owner a section that errors on every screen inside
 * it; fail-closed costs her a second of a section she cannot use yet. The asymmetry is the
 * whole argument, and it is why there is no `undefined` in this module's public surface:
 * a tri-state invites `v-if="provisioned !== false"` at a call site, which is fail-open
 * spelled defensively.
 *
 * THE ANSWER IS CACHED FOR THE LIFE OF THE PAGE, both outcomes. Provisioning is an
 * operator action that happens minutes-to-days before the owner looks, not something that
 * flips mid-session — and re-asking on every sidebar re-render would put one XHR behind
 * every navigation. The cost is that a workspace provisioned WHILE its owner has the tab
 * open needs a reload; that is the same reload she needs for the tenant's other state.
 *
 * A TEAM MEMBER ASKS THE MEMBER ROUTE (P-1.7). Staff get the chat pane and nothing else of
 * Waslo AI; see `useWasloProvisioned` below for why this answer cannot open a page to them.
 */

import { computed, getCurrentInstance, reactive, watchEffect } from 'vue';

import { wasloRequest } from '../api/client.js';
import { useWasloAccountId } from './useWasloAccountId.js';

/** accountId → boolean, from the ADMIN route. `reactive` so a resolved fetch re-renders. */
const outcomes = reactive(new Map());
/** accountId → Promise<boolean>. Collapses the N call sites on one page into one call. */
const inFlight = new Map();
/**
 * accountId → boolean, from the MEMBER route (P-1.7): a team member's own answer, which mounts
 * the chat panel for her. It never feeds the route guard, which stays on `outcomes`.
 */
const memberOutcomes = reactive(new Map());
const memberInFlight = new Map();

/** Specs only — module state outlives a test file otherwise. */
export const resetWasloProvisioning = () => {
  outcomes.clear();
  inFlight.clear();
  memberOutcomes.clear();
  memberInFlight.clear();
};

const positiveInt = value => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const askOnce = (rawAccountId, cache, flights, options) => {
  const accountId = positiveInt(rawAccountId);
  if (accountId === null) return Promise.resolve(false);
  if (cache.has(accountId)) return Promise.resolve(cache.get(accountId));

  const existing = flights.get(accountId);
  if (existing) return existing;

  const flight = wasloRequest(accountId, '/provisioning', options)
    .then(body => body?.provisioned === true)
    // Every failure is the same answer. `not_provisioned` is the expected one; the rest
    // are a bad session, a broken network or a BFF that is down, and none of them is a
    // reason to show a section whose every screen would fail the same way.
    .catch(() => false)
    .then(result => {
      cache.set(accountId, result);
      flights.delete(accountId);
      return result;
    });

  flights.set(accountId, flight);
  return flight;
};

/**
 * The imperative form, for callers with no component instance: the route guard. The ADMIN
 * answer, always: a team member is never let into a Waslo page by this.
 *
 * @param {number|string} rawAccountId
 * @returns {Promise<boolean>}
 */
export const isWasloProvisioned = rawAccountId =>
  askOnce(rawAccountId, outcomes, inFlight, {});

/**
 * The member answer (P-1.7), for the chat panel of a team member.
 *
 * @param {number|string} rawAccountId
 * @returns {Promise<boolean>}
 */
export const isWasloProvisionedForMember = rawAccountId =>
  askOnce(rawAccountId, memberOutcomes, memberInFlight, { scope: 'member' });

/**
 * The reactive form, for components.
 *
 * AN ADMINISTRATOR ASKS THE ADMIN ROUTE; A TEAM MEMBER ASKS THE MEMBER ROUTE (P-1.7), so the
 * Waslo section of the chat pane mounts for her too. That does NOT give her the Waslo pages:
 * every one of them is `meta.permissions: ['administrator']`, Chatwoot's sidebar drops the
 * group and the two Campaigns rows for her on that alone, and the route guard reads the admin
 * answer. `specs/member-access.spec.js` pins all three.
 *
 * @returns {import('vue').ComputedRef<boolean>} False until proven otherwise.
 */
export const useWasloProvisioned = () => {
  const accountId = useWasloAccountId();
  // Not `useAdmin()`: it goes through `useStoreGetters`, which throws with no store
  // installed — and this composable is read from `Sidebar.vue`, from `ContactPanel.vue`
  // and from specs that install neither. Same escape hatch, and same reason, as
  // `useWasloAccountId`.
  const instance = getCurrentInstance();
  const store = instance?.proxy?.$store ?? null;
  // No store means no way to read a role, which happens only in a harness. Asking the admin
  // route is the safe half of that guess: the BFF answers 403 to a non-admin either way.
  const role = computed(() =>
    store === null ? 'administrator' : (store.getters?.getCurrentRole ?? null)
  );

  watchEffect(() => {
    if (accountId.value === null) return;
    if (role.value === 'administrator') isWasloProvisioned(accountId.value);
    else if (role.value === 'agent') {
      isWasloProvisionedForMember(accountId.value);
    }
  });

  return computed(() => {
    if (accountId.value === null) return false;
    if (role.value === 'administrator') {
      return outcomes.get(accountId.value) === true;
    }
    if (role.value === 'agent') {
      return memberOutcomes.get(accountId.value) === true;
    }
    return false;
  });
};

export default useWasloProvisioned;
